import cx from 'classnames';
import dynamic from 'next/dynamic';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useNavUserInformationQuery } from 'api';
import { useAuthenticated } from 'lib/auth';

import Divider from 'components/divider';
import { Heading } from 'components/typography';
import { Icon } from 'components/@tedui';

import TalkTypes from '../talk-types';
import TalkDescription from '../TalkDetails/TalkDescription';

const LearnModuleList = dynamic(
  () => import('../LearnModuleList/LearnModuleList')
);
const TalkTypeDescription = dynamic(() => import('../TalkTypeDescription'));
const Comments = dynamic(() => import('../Comments'));
const TakeActionList = dynamic(() => import('../TakeActionList'));
const SpeakerDetails = dynamic(() => import('../SpeakerDetails'));
const TalkEventBlurb = dynamic(() => import('../TalkEventBlurb'));
const SpeakerRecommendations = dynamic(
  () => import('../SpeakerRecommendations')
);

const TalkPageContent = ({
  videoData,
  displayComments,
  setIsSignInModalOpen
}) => {
  const [isTalkDetailsRollupVisible, setIsTalkDetailsRollupVisible] =
    useState(false);

  const isLoggedIn = useAuthenticated();
  const { data } = useNavUserInformationQuery({
    ssr: false,
    skip: !isLoggedIn
  });

  const userId = data?.viewer?.id;

  const showSpeakerDetails = useMemo(() => {
    const isTedXTalk = videoData.type.id === '2';
    return !isTedXTalk || videoData.curatorApproved;
  }, [videoData.curatorApproved, videoData.type.id]);

  return (
    <div>
      <div className="mt-4 flex items-center justify-between lg:hidden">
        <button
          onClick={() =>
            setIsTalkDetailsRollupVisible(!isTalkDetailsRollupVisible)
          }
          type="button"
          className="flex w-full items-center justify-between"
        >
          <Heading size="m">
            <FormattedMessage defaultMessage="Talk details" />
          </Heading>
          <Icon
            iconName={
              isTalkDetailsRollupVisible ? 'chevron-up' : 'chevron-down'
            }
            className="flex items-center justify-center text-2xl"
          />
        </button>
      </div>
      <Divider className="mb-4 mt-3 lg:hidden" />
      <div
        className={cx('lg:block', {
          hidden: !isTalkDetailsRollupVisible
        })}
      >
        <div className="lg:hidden">
          <TalkDescription
            description={videoData.description}
            featured={videoData.featured}
            topics={videoData.topics.nodes}
          />
          <Divider className="mb-4" />
          <TalkEventBlurb
            id={videoData.id}
            type={videoData.type}
            featured={videoData.featured}
            videoContext={videoData.videoContext}
            partnerName={videoData.partnerName}
            className="lg:hidden"
          />
          <TakeActionList
            className="lg:hidden"
            takeActionItems={videoData.talkExtras.takeAction}
          />
        </div>

        {videoData.type.name &&
          videoData.type.id === TalkTypes.CustomSponsoredContent && (
            <TalkTypeDescription type={videoData.type} id={videoData.id} />
          )}

        {showSpeakerDetails && (
          <SpeakerDetails
            speakers={videoData.speakers.nodes}
            videoContext={videoData.videoContext}
          />
        )}
        {videoData.talkExtras.recommendations.length > 0 && (
          <SpeakerRecommendations
            speakerName={videoData.presenterDisplayName}
            recommendations={videoData.talkExtras.recommendations}
          />
        )}
        {videoData.talkExtras.learnModules.length > 0 && (
          <LearnModuleList
            learnModuleItems={videoData.talkExtras.learnModules}
          />
        )}
        {videoData.type.name &&
          videoData.type.id !== TalkTypes.CustomSponsoredContent && (
            <TalkTypeDescription type={videoData.type} id={videoData.id} />
          )}
      </div>
      <TalkEventBlurb
        id={videoData.id}
        type={videoData.type}
        featured={videoData.featured}
        videoContext={videoData.videoContext}
        partnerName={videoData.partnerName}
        className="hidden lg:block"
      />

      {
        // Initialize OpenWeb Conversation Container
        displayComments && (
          <Comments
            id={videoData.id}
            isLoggedIn={isLoggedIn}
            userId={userId}
            setIsSignInModalOpen={setIsSignInModalOpen}
            slug={videoData.slug}
            topics={videoData.topics}
          />
        )
      }
      <TakeActionList
        className="hidden lg:block"
        takeActionItems={videoData.talkExtras.takeAction}
      />
    </div>
  );
};

export default TalkPageContent;
